import React, {useEffect, useState} from 'react';
import {Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios, {AxiosResponse} from 'axios';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import SearchIndexModal from './SearchIndexModal';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {useAlert} from '@/components/alerts';
import {buildURL} from '@/utils/urlBuilder';
import {extractErrorMessage} from '@/utils/errorHandling';

interface SearchIndexDataItem {
    date: string;
    events_count: number;
    doc_count: number;
    index_percentage: number;
}

interface SearchIndexStats {
    es_document_count: number;
    count: number;
}

interface SearchIndexResponse {
    searchIndexData: SearchIndexDataItem[];
    searchIndexStats: SearchIndexStats;
    searchIndexBlockedStatusActive: boolean;
}

const searchIndexDataUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<AxiosResponse<SearchIndexResponse>> =>
    axios.get(url, {params: paramsDict})
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const searchIndexDataUpdateRequestDebounced = AwesomeDebouncePromise(searchIndexDataUpdateRequest, 350);

const SearchIndexFullListView: React.FC = React.memo(() => {
    const {showSuccess, showError} = useAlert();
    const {userData, config, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [loadingInsert, setLoadingInsert] = useState<Record<string, boolean>>({});
    const [loadingInsertMissing, setLoadingInsertMissing] = useState<Record<string, boolean>>({});

    const searchIndexData = useStoreState((state: any) => state.searchIndexData.items);
    const storeSearchIndexData = useStoreActions((actions: any) => actions.searchIndexData.loadSearchIndexData);

    const searchIndexStats = useStoreState((state: any) => state.searchIndexData.stats);
    const storeSearchIndexStats = useStoreActions((actions: any) => actions.searchIndexData.loadSearchIndexStats);

    const searchIndexStatus = useStoreState((state: any) => state.searchIndexData.status);
    const storeSearchIndexStatus = useStoreActions((actions: any) => actions.searchIndexData.setStatus);

    const loadingSearchIndexData = useStoreState((state: any) => state.searchIndexData.loading);
    const setLoadingSearchIndexData = useStoreActions((actions: any) => actions.searchIndexData.setLoading);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setLoadingSearchIndexData(true);

            const paramsDict: Record<string, any> = {};

            const response = await searchIndexDataUpdateRequestDebounced(
                buildURL('searchIndexResource'), paramsDict,
            );

            storeSearchIndexData(response.data.searchIndexData);
            storeSearchIndexStats(response.data.searchIndexStats);
            storeSearchIndexStatus(response.data.searchIndexBlockedStatusActive);

            setLoadingSearchIndexData(false);
        };

        fetchData();
    }, [
        storeSearchIndexData,
        storeSearchIndexStats,
        storeSearchIndexStatus,
        setLoadingSearchIndexData,
    ]);

    const userCanAddToIndexBackgroundDate = hasPermission('fs.search_registry.insert.execute', userData?.permissions, true);
    const userCanInsertMissingToIndex = hasPermission('fs.search_registry.insert.execute', userData?.permissions, true);
    const userCanAccessSearchIndexData = hasPermission('fs.search_registry.read', userData?.permissions, true);

    if (!userCanAccessSearchIndexData && !loadingUserData) {
        return (
            <>
                <ConditionalNav>

                </ConditionalNav>

                <HelpTextBlock helpText={i18n.t('ht_index_collection.text')} />

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
    };

    const handleInsertDate = async (date: string): Promise<void> => {
        if (!userCanAddToIndexBackgroundDate) {return;}

        const key = `insert_${date}`;
        setLoadingInsert(prev => ({...prev, [key]: true}));

        try {
            const response = await axios.post(buildURL('insertDateVisionIndexDocs', {date}));
            showSuccess('Index insertion started successfully');
        } catch (error) {
            console.error('Error inserting to index:', error);
            const errorMessage = extractErrorMessage(error, 'Error starting index insertion. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingInsert(prev => ({...prev, [key]: false}));
        }
    };

    const handleInsertMissing = async (date: string): Promise<void> => {
        if (!userCanInsertMissingToIndex) {return;}

        const key = `missing_${date}`;
        setLoadingInsertMissing(prev => ({...prev, [key]: true}));

        try {
            const response = await axios.post(buildURL('insertMissingVisionIndexDocs', {start_date: date, end_date: date}));
            showSuccess('Missing index insertion started successfully');
        } catch (error) {
            console.error('Error inserting missing to index:', error);
            const errorMessage = extractErrorMessage(error, 'Error starting missing index insertion. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingInsertMissing(prev => ({...prev, [key]: false}));
        }
    };

    const columns = [
        {
            accessorKey: 'date',
            header: i18n.t('th_date.text'),
            cell: (cell: any) => {
                if (cell === '-') {
                    return cell;
                }

                return (
                    <FormattedDateTime
                        dateTime={cell.getValue()}
                        showTime={false}
                        options={{setZone: false}}
                    />
                );
            },
        },
        {
            accessorKey: 'events_count',
            header: i18n.t('th_event_count.text'),
            sort: false,
        },
        {
            accessorKey: 'doc_count',
            header: i18n.t('th_index_count.text'),
            sort: false,
        },
        {
            accessorKey: 'index_percentage',
            header: i18n.t('th_search_index.text'),
            cell: (cell: any) => {
                if (cell.getValue() === '-') {
                    return cell.getValue();
                }

                let progressBarStyle: string;
                if (cell.getValue() === 100) {
                    progressBarStyle = 'bg-success';
                } else if (cell.getValue() === 0) {
                    progressBarStyle = 'bg-danger';
                } else {
                    progressBarStyle = 'bg-warning';
                }

                const progressPercentage = cell.getValue().toFixed(2);
                return (
                    <>
                        <span>
                            {progressPercentage}
                            %
                        </span>
                        <div className="progress progress-xs margin-vertical-10">
                            <div
                                className={`progress-bar ${progressBarStyle}`}
                                style={{width: `${cell.getValue()}%`, height: '6px'}}
                            />
                        </div>
                    </>
                );
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                const date = cell.row.original.date;
                const insertKey = `insert_${date}`;
                const missingKey = `missing_${date}`;

                return (
                    <div className="d-flex justify-content-start align-items-center" style={{gap: '8px'}}>
                        {userCanAddToIndexBackgroundDate ? (
                            <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleInsertDate(date)}
                                disabled={loadingInsert[insertKey]}
                                style={{whiteSpace: 'nowrap', minWidth: '80px'}}
                                data-cy="search-index-full-list-success-btn-1"
                            >
                                {loadingInsert[insertKey] ? 'Inserting...' : i18n.t('jt_insert.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <Button
                                    variant="success"
                                    size="sm"
                                    disabled
                                    style={{whiteSpace: 'nowrap', minWidth: '80px'}}
                                    data-cy="search-index-full-list-success-btn-1"
                                >
                                    {i18n.t('jt_insert.text')}
                                </Button>
                            </Tooltip>
                        )}

                        {userCanInsertMissingToIndex ? (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleInsertMissing(date)}
                                disabled={loadingInsertMissing[missingKey]}
                                style={{whiteSpace: 'nowrap', minWidth: '120px'}}
                                data-cy="search-index-full-list-success-btn"
                            >
                                {loadingInsertMissing[missingKey] ? 'Inserting...' : i18n.t('jt_insert_missing.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled
                                    style={{whiteSpace: 'nowrap', minWidth: '120px'}}
                                    data-cy="search-index-full-list-success-btn"
                                >
                                    {i18n.t('jt_insert_missing.text')}
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <ConditionalNav>

            </ConditionalNav>

            <HelpTextBlock helpText={i18n.t('ht_index_collection.text')} />

            <Row>
                <div className="col-md-6 col-lg-3 col-xlg-3">
                    <div className="card">
                        <div className="box p-2 rounded bg-info text-center">
                            <h3 className="fw-light text-white">{searchIndexStats?.es_document_count || 0}</h3>
                            <h6 className="text-white">{i18n.t('jt_searchable_events.text')}</h6>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-lg-3 col-xlg-3">
                    <div className="card">
                        <div className="box p-2 rounded bg-info text-center">
                            <h3 className="fw-light text-white">{searchIndexStats?.count || 0}</h3>
                            <h6 className="text-white">{i18n.t('jt_searchable_event.text')}</h6>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-lg-3 col-xlg-3">
                    <div className="card">
                        <div
                            className={`box p-2 rounded bg-info text-center ${searchIndexStatus !== true ? 'bg-success' : 'bg-warning'}`}>
                            <h3 className="fw-light text-white">{searchIndexStatus !== true ? 'Active' : 'Blocked'}</h3>
                            <h6 className="text-white">{i18n.t('jt_index_active.text')}</h6>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-lg-3 col-xlg-3">
                    <div className="card">
                        <div className="box p-2 rounded bg-info text-center">
                            <h3 className="fw-light text-white">{searchIndexStatus !== true ? 'Ready for new events' : 'Blocked for new events'}</h3>
                            <h6 className="text-white">{i18n.t('jt_index_status.text')}</h6>
                        </div>
                    </div>
                </div>
            </Row>

            {
                loadingSearchIndexData ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <>
                        <div className="card">
                            <div className="card-body">
                                <TableComponent data={searchIndexData} columns={columns} />
                            </div>
                        </div>
                        <SearchIndexModal
                            isOpen={settingsModalOpen}
                            onClose={closeSettingsModal}
                        />
                    </>
                )
            }
        </>
    );
});

export default SearchIndexFullListView;

